import type * as React from "react";

import BrianMarkArt from "./brian-mark.svg?react";
import { cn } from "../lib/cn";

export type BrianWordmarkTone = "paper" | "desk";

export interface BrianWordmarkProps {
  tone?: BrianWordmarkTone;
  mark?: boolean;
  className?: string;
  ref?: React.Ref<HTMLDivElement>;
}

const toneInk: Record<
  BrianWordmarkTone,
  { text: string; vet: string; ia: string }
> = {
  paper: {
    text: "text-ink",
    vet: "text-annotation",
    ia: "text-accent",
  },
  // accent-lite: 7.0:1 on espresso.
  desk: {
    text: "text-ink-inverse",
    vet: "text-ink-on-nav-muted",
    ia: "text-accent-lite",
  },
};

export interface BrianGlyphProps {
  tone?: BrianWordmarkTone;
  className?: string;
}

/** Brian's mark without the disc, for the wordmark and the collapsed nav; decorative, so label its container. */
export function BrianGlyph({ tone = "paper", className }: BrianGlyphProps) {
  const ink = toneInk[tone];
  return (
    <BrianMarkArt
      aria-hidden="true"
      focusable="false"
      className={cn("shrink-0", ink.text, className)}
    />
  );
}

export function BrianWordmark({
  tone = "paper",
  mark = true,
  className,
  ref,
}: BrianWordmarkProps) {
  const ink = toneInk[tone];
  return (
    <div
      ref={ref}
      role="img"
      aria-label="Brian.vet"
      className={cn(
        "inline-flex items-center gap-[0.3em] font-display text-[2rem] leading-none",
        ink.text,
        className,
      )}
    >
      {mark && <BrianGlyph tone={tone} className="h-[1.4em] w-[1.4em]" />}
      <span
        aria-hidden="true"
        className="whitespace-nowrap font-semibold tracking-[-0.055em]"
      >
        Br<span className={ink.ia}>ia</span>n
        <span className={cn("font-normal tracking-[-0.045em]", ink.vet)}>
          .vet
        </span>
      </span>
    </div>
  );
}
